import { Button, Switch, Textarea, cn } from '@design-system';
import type { EditableReply, EmailTestResult } from '../api/api';
import { reasonInWords } from '../conversationWords';
import { useReplyChance } from '../hooks/use-reply-chance';

const percent = (value: number | null) => (value === null ? '–' : `${Math.round(value * 100)}%`);

function Verdict({ result }: { result: EmailTestResult }) {
  const target = result.target;
  if (!target) return null;
  if (target.chosen) return <span className="font-semibold text-[#1B6B3A]">Picks this reply</span>;
  if (result.outcome === 'reply' && result.reply)
    return <span className="font-semibold text-[#8A4B00]">Picks “{result.reply.title}” instead</span>;
  return <span className="font-semibold text-[#8A4B00]">Goes to our team: {reasonInWords(result.reason)}</span>;
}

/**
 * Whether advisors' emails would reach this reply: one typed email, or every example it lists, tried
 * against the other replies with this reply's current, unsaved words.
 */
export function ReplyChance({ entryId, reply }: { entryId: string; reply: EditableReply }) {
  const chance = useReplyChance(entryId, reply);

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-zinc-200 bg-white px-5 py-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold">Will emails pick this reply?</h2>
        <p className="text-[13px] text-zinc-600">
          Try an email an advisor might send. Uses your unsaved changes; nothing is sent.
        </p>
      </div>
      <Textarea
        rows={3}
        aria-label="An email to try"
        placeholder="e.g. Has my client finished their intake yet?"
        value={chance.email}
        onChange={event => chance.setEmail(event.target.value)}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={!chance.email.trim() || chance.checking}
          onClick={chance.check}
        >
          {chance.checking ? 'Checking…' : 'Check'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={chance.exampleCount === 0 || chance.checking}
          onClick={chance.checkExamples}
        >
          Check its {chance.exampleCount} {chance.exampleCount === 1 ? 'example' : 'examples'}
        </Button>
        <label className="ml-auto flex items-center gap-2 text-[13px] text-zinc-700">
          With drafts
          <Switch
            checked={chance.includeDrafts}
            onCheckedChange={chance.setIncludeDrafts}
          />
        </label>
      </div>

      {chance.results.length > 0 && (
        <ul className="flex flex-col divide-y divide-zinc-100 rounded-lg border border-zinc-200">
          {chance.results.map(({ email, result }) => (
            <li
              key={email}
              className="flex flex-col gap-1 px-3 py-2.5 text-sm"
            >
              <span className="line-clamp-2 text-zinc-700">“{email}”</span>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <Verdict result={result} />
                <span
                  className={cn('tabular-nums text-[13px]', result.target?.chosen ? 'text-[#1B6B3A]' : 'text-zinc-600')}
                  title={`Kind of request ${percent(result.target?.categoryChance ?? null)} × this reply among its kind ${percent(result.target?.withinCategoryChance ?? null)}`}
                >
                  Chance {percent(result.target?.chance ?? null)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
